import { useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { Button, Modal, Rating, Textarea } from '@praticabr/ppsa';

import * as Styled from './styles';

export type SurveySatisfactionAnswer = {
  score: number;
  comment: string;
};

type ModalProps = {
  onCancel: () => void;
  surveySubject: string;
  surveyDescription?: string | null;
  onSubmit?: (answer: SurveySatisfactionAnswer) => Promise<void> | void;
};

const satisfactionOptions: {
  score: number;
  labelId: string;
}[] = [
    { score: 1, labelId: 'csat.Muito insatisfeito' },
    { score: 2, labelId: 'csat.Insatisfeito' },
    { score: 3, labelId: 'csat.Neutro' },
    { score: 4, labelId: 'csat.Satisfeito' },
    { score: 5, labelId: 'csat.Muito satisfeito' },
  ];

const normalizeMessageKey = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const ModalSurveySatisfaction: React.FC<ModalProps> = ({
  onCancel,
  surveySubject,
  surveyDescription,
  onSubmit,
}) => {
  const intl = useIntl();
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolveSurveyText = (value?: string | null) => {
    if (!value) {
      return '';
    }

    if (Object.prototype.hasOwnProperty.call(intl.messages, value)) {
      return intl.formatMessage({ id: value });
    }

    const normalizedValue = normalizeMessageKey(value);
    const matchingKey = Object.keys(intl.messages).find(
      messageKey => normalizeMessageKey(messageKey) === normalizedValue
    );

    if (matchingKey) {
      return intl.formatMessage({ id: matchingKey });
    }

    return value;
  };

  const resolvedSurveySubject = resolveSurveyText(surveySubject).trim();
  const selectedOption = satisfactionOptions.find(option => option.score === selectedScore);
  const questionSuffix =
    resolvedSurveySubject && /[?.!]$/.test(resolvedSurveySubject) ? '' : '?';

  const handleSubmit = async () => {
    try {
      if (selectedScore == null) {
        toast.error(
          intl.formatMessage({ id: 'csat.Selecione uma nota para continuar' }).toUpperCase()
        );
        return;
      }

      setIsSubmitting(true);

      if (onSubmit) {
        await onSubmit({
          score: selectedScore,
          comment: comment.trim(),
        });
      }

      toast.success(
        intl.formatMessage({ id: 'csat.Obrigado por responder à nossa pesquisa!' }).toUpperCase()
      );

      onCancel();
    } catch (error) {
      console.error(error);
      toast.error(
        intl
          .formatMessage({ id: 'equipment.ocorreu um erro. Tente novamente mais tarde' })
          .toUpperCase()
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal.root>
      <Modal.header
        title={intl.formatMessage({ id: 'csat.AVALIE SUA EXPERIÊNCIA' })}
        variant='gradient'
      />
      <Modal.body>
        <Modal.body.main>
          <Styled.Content>
            <Styled.ModalMain>
              <Styled.Question>
                {intl.formatMessage({ id: 'csat.Como você avalia sua experiência com' })}
                {resolvedSurveySubject ? (
                  <>
                    {' '}
                    <Styled.QuestionHighlight>{resolvedSurveySubject}</Styled.QuestionHighlight>
                    {questionSuffix}
                  </>
                ) : null}
              </Styled.Question>

              <Styled.Options>
                <Styled.RatingWrapper>
                  <Rating
                    variant='satisfaction'
                    value={selectedScore ?? 0}
                    onChange={value => setSelectedScore(value)}
                    editable={!isSubmitting}
                    size={42}
                    labels={satisfactionOptions.map(option => intl.formatMessage({ id: option.labelId }))}
                  />
                </Styled.RatingWrapper>
              </Styled.Options>

              <Styled.SuggestionSection>
                <Styled.SuggestionLabel>
                  {intl.formatMessage({ id: 'csat.Utilize o espaço abaixo para deixar suas sugestões:' })}
                </Styled.SuggestionLabel>
                <Textarea>
                  <Textarea.Field
                    id='csat-comment'
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'csat.Digite aqui' })}
                    rows={4}
                  />
                </Textarea>
              </Styled.SuggestionSection>
            </Styled.ModalMain>
          </Styled.Content>
        </Modal.body.main>

        <Modal.body.actions>
          <Styled.ModalActions>
            <Button
              title={intl.formatMessage({ id: 'csat.Enviar' })}
              style={{ width: '108px' }}
              type='button'
              size='lg'
              variant='confirmation-modal'
              onClick={handleSubmit}
            />
          </Styled.ModalActions>
        </Modal.body.actions>
      </Modal.body>
    </Modal.root>
  );
};

export default ModalSurveySatisfaction;
